'use client';

import React, { useMemo, useState } from 'react';
import {
  BarChart3,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  FileText,
  FolderOpen,
  GitMerge,
  Headphones,
  Layers,
  LayoutGrid,
  Puzzle,
  Shield,
  Users,
  Workflow,
  Zap,
} from 'lucide-react';
import { NavFilter, nodeMatchesNavFilter } from '@/lib/specmap/filters';
import { LaneData, NodeData } from '@/lib/specmap/types';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  activeLanes: string[];
  onToggleLane: (lane: string) => void;
  selectedNodeId: string;
  onNodeSelect: (id: string) => void;
  nodes: NodeData[];
  lanes: LaneData[];
  activeNavItem: NavFilter;
  onNavChange: (nav: NavFilter) => void;
}

const LANE_ICON_BY_ID = {
  user: Users,
  admin: Shield,
  support: Headphones,
  external: Zap,
};

const NAV_SECTIONS: {
  id: string;
  label: string;
  items: { id: NavFilter; label: string; icon: typeof LayoutGrid }[];
}[] = [
  {
    id: 'nav-architecture',
    label: 'Architecture',
    items: [
      { id: 'nav-overview', label: 'Overview', icon: LayoutGrid },
      { id: 'nav-modules', label: 'Module Groups', icon: Layers },
      { id: 'nav-workflows', label: 'Workflows', icon: Workflow },
    ],
  },
  {
    id: 'nav-structure',
    label: 'Structure',
    items: [
      { id: 'nav-screens', label: 'Screens', icon: FileText },
      { id: 'nav-roles', label: 'Roles', icon: Users },
      { id: 'nav-integrations', label: 'Integrations', icon: Puzzle },
      { id: 'nav-dependencies', label: 'Dependencies', icon: GitMerge },
    ],
  },
  {
    id: 'nav-reports',
    label: 'Reports',
    items: [
      { id: 'nav-progress', label: 'Progress Report', icon: BarChart3 },
      { id: 'nav-docs', label: 'Documentation', icon: FolderOpen },
    ],
  },
];

const STATUS_COLOR: Record<string, string> = {
  Draft: '#8A7F78',
  'In Review': '#F59E0B',
  Approved: '#60A5FA',
  Complete: '#4ADE80',
  Deprecated: '#F87171',
};

export default function SpecMapSidebar({
  collapsed,
  onToggleCollapse,
  activeLanes,
  onToggleLane,
  selectedNodeId,
  onNodeSelect,
  nodes,
  lanes,
  activeNavItem,
  onNavChange,
}: SidebarProps) {
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'nav-architecture',
    'nav-structure',
  ]);
  const [lanesExpanded, setLanesExpanded] = useState(true);

  const activeNodes = nodes.filter((node) => !node.archivedAt);
  const navCounts = useMemo(() => {
    const roles = new Set(activeNodes.map((node) => node.owner).filter(Boolean));
    const integrations = new Set<string>();

    activeNodes.forEach((node) => {
      if (node.category === 'Integration') {
        integrations.add(node.id);
      }
      node.dependencies.forEach((dependency) => {
        if (dependency.label.toLowerCase().includes('integration')) {
          integrations.add(dependency.label);
        }
      });
    });

    return {
      'nav-overview': activeNodes.length,
      'nav-modules': activeNodes.filter((node) => nodeMatchesNavFilter(node, 'nav-modules')).length,
      'nav-workflows': activeNodes.filter((node) => nodeMatchesNavFilter(node, 'nav-workflows'))
        .length,
      'nav-screens': activeNodes.reduce((total, node) => total + node.childScreens.length, 0),
      'nav-roles': roles.size,
      'nav-integrations': integrations.size,
      'nav-dependencies': activeNodes.reduce((total, node) => total + node.dependencies.length, 0),
      'nav-progress': activeNodes.length,
      'nav-docs': activeNodes.filter((node) => nodeMatchesNavFilter(node, 'nav-docs')).length,
    };
  }, [activeNodes]);

  const laneCounts = useMemo(
    () =>
      lanes.reduce<Record<string, number>>((counts, lane) => {
        counts[lane.id] = activeNodes.filter((node) => node.lane === lane.id).length;
        return counts;
      }, {}),
    [activeNodes, lanes]
  );
  const recentNodes: NodeData[] = [...activeNodes]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  const toggleSection = (id: string) => {
    setExpandedSections((prev) =>
      prev.includes(id) ? prev.filter((section) => section !== id) : [...prev, id]
    );
  };

  return (
    <aside data-component-id="src/app/components/SpecMapSidebar.tsx:154:4" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="154" data-component-end-line="320" data-component-file="SpecMapSidebar.tsx" data-component-name="aside" data-component-content="%7B%22elementName%22%3A%22aside%22%7D"
      className={`sidebar-bg flex-shrink-0 flex flex-col transition-all duration-300 ease-in-out overflow-hidden ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      <div data-component-id="src/app/components/SpecMapSidebar.tsx:159:6" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="159" data-component-end-line="167" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-end%20px-2%20py-2%20border-b%20border-border%22%7D" className="flex items-center justify-end px-2 py-2 border-b border-border">
        <button data-component-id="src/app/components/SpecMapSidebar.tsx:160:8" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="160" data-component-end-line="166" data-component-file="SpecMapSidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22p-1.5%20rounded-lg%20text-muted-foreground%20hover%3Atext-foreground%20hover%3Abg-muted%2F40%20transition-all%20active%3Ascale-95%22%7D"
          onClick={onToggleCollapse}
          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all active:scale-95"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight data-component-id="src/app/components/SpecMapSidebar.tsx:165:23" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="165" data-component-end-line="165" data-component-file="SpecMapSidebar.tsx" data-component-name="ChevronRight" data-component-content="%7B%22elementName%22%3A%22ChevronRight%22%7D" size={15} /> : <ChevronLeft data-component-id="src/app/components/SpecMapSidebar.tsx:165:52" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="165" data-component-end-line="165" data-component-file="SpecMapSidebar.tsx" data-component-name="ChevronLeft" data-component-content="%7B%22elementName%22%3A%22ChevronLeft%22%7D" size={15} />}
        </button>
      </div>

      <div data-component-id="src/app/components/SpecMapSidebar.tsx:169:6" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="169" data-component-end-line="307" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20overflow-y-auto%20overflow-x-hidden%20py-2%22%7D" className="flex-1 overflow-y-auto overflow-x-hidden py-2">
        {NAV_SECTIONS.map((section) => (
          <div data-component-id="src/app/components/SpecMapSidebar.tsx:171:10" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="171" data-component-end-line="219" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-1%22%7D" key={section.id} className="mb-1">
            {!collapsed && (
              <button data-component-id="src/app/components/SpecMapSidebar.tsx:173:14" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="173" data-component-end-line="184" data-component-file="SpecMapSidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20justify-between%20px-3%20py-1.5%20text-2xs%20font-semibold%20uppercase%20tracking-widest%20text-muted-foreground%20hover%3Atext-foreground%2F70%20transition-colors%22%7D"
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-2xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground/70 transition-colors"
              >
                <span data-component-id="src/app/components/SpecMapSidebar.tsx:177:16" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="177" data-component-end-line="177" data-component-file="SpecMapSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{section.label}</span>
                <ChevronDown data-component-id="src/app/components/SpecMapSidebar.tsx:178:16" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="178" data-component-end-line="183" data-component-file="SpecMapSidebar.tsx" data-component-name="ChevronDown" data-component-content="%7B%22elementName%22%3A%22ChevronDown%22%7D"
                  size={11}
                  className={`transition-transform ${
                    expandedSections.includes(section.id) ? 'rotate-180' : ''
                  }`}
                />
              </button>
            )}

            {(collapsed || expandedSections.includes(section.id)) &&
              section.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeNavItem === item.id;
                const badge = navCounts[item.id];
                return (
                  <button data-component-id="src/app/components/SpecMapSidebar.tsx:193:18" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="193" data-component-end-line="216" data-component-file="SpecMapSidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                    key={item.id}
                    onClick={() => onNavChange(item.id)}
                    title={collapsed ? item.label : undefined}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg mx-1 transition-all active:scale-[0.98] group ${
                      collapsed ? 'justify-center mx-1 w-[calc(100%-8px)]' : 'w-[calc(100%-8px)]'
                    } ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                    }`}
                  >
                    <Icon data-component-id="src/app/components/SpecMapSidebar.tsx:205:20" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="205" data-component-end-line="205" data-component-file="SpecMapSidebar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22flex-shrink-0%22%7D" size={15} className="flex-shrink-0" />
                    {!collapsed && (
                      <>
                        <span data-component-id="src/app/components/SpecMapSidebar.tsx:208:24" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="208" data-component-end-line="210" data-component-file="SpecMapSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-medium%20flex-1%20text-left%20truncate%22%7D" className="text-xs font-medium flex-1 text-left truncate">
                          {item.label}
                        </span>
                        <span data-component-id="src/app/components/SpecMapSidebar.tsx:211:24" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="211" data-component-end-line="213" data-component-file="SpecMapSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-2xs%20px-1.5%20py-0.5%20rounded%20font-tabular%20bg-muted%20text-muted-foreground%22%7D" className="text-2xs px-1.5 py-0.5 rounded font-tabular bg-muted text-muted-foreground">
                          {badge}
                        </span>
                      </>
                    )}
                  </button>
                );
              })}
          </div>
        ))}

        {!collapsed && (
          <div data-component-id="src/app/components/SpecMapSidebar.tsx:223:10" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="223" data-component-end-line="274" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%20mb-1%22%7D" className="mt-2 mb-1">
            <button data-component-id="src/app/components/SpecMapSidebar.tsx:224:12" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="224" data-component-end-line="233" data-component-file="SpecMapSidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20justify-between%20px-3%20py-1.5%20text-2xs%20font-semibold%20uppercase%20tracking-widest%20text-muted-foreground%20hover%3Atext-foreground%2F70%20transition-colors%22%7D"
              onClick={() => setLanesExpanded((prev) => !prev)}
              className="w-full flex items-center justify-between px-3 py-1.5 text-2xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground/70 transition-colors"
            >
              <span data-component-id="src/app/components/SpecMapSidebar.tsx:228:14" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="228" data-component-end-line="228" data-component-file="SpecMapSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Lanes%22%7D">Lanes</span>
              <ChevronDown data-component-id="src/app/components/SpecMapSidebar.tsx:229:14" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="229" data-component-end-line="232" data-component-file="SpecMapSidebar.tsx" data-component-name="ChevronDown" data-component-content="%7B%22elementName%22%3A%22ChevronDown%22%7D"
                size={11}
                className={`transition-transform ${lanesExpanded ? 'rotate-180' : ''}`}
              />
            </button>

            {lanesExpanded && (
              <div data-component-id="src/app/components/SpecMapSidebar.tsx:236:14" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="236" data-component-end-line="272" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-1%20space-y-0.5%20animate-fade-in%22%7D" className="px-1 space-y-0.5 animate-fade-in">
                {lanes.map((lane) => {
                  const Icon = LANE_ICON_BY_ID[lane.id as keyof typeof LANE_ICON_BY_ID] ?? Layers;
                  const isActive = activeLanes.includes(lane.id);
                  const laneColor = lane.color ?? '#0F9B8E';
                  return (
                    <button data-component-id="src/app/components/SpecMapSidebar.tsx:242:20" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="242" data-component-end-line="269" data-component-file="SpecMapSidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20gap-2.5%20px-2%20py-1.5%20rounded-lg%20transition-all%20hover%3Abg-muted%2F30%20active%3Ascale-%5B0.98%5D%22%7D"
                      key={`lane-toggle-${lane.id}`}
                      onClick={() => onToggleLane(lane.id)}
                      className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg transition-all hover:bg-muted/30 active:scale-[0.98]"
                    >
                      <div data-component-id="src/app/components/SpecMapSidebar.tsx:247:22" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="247" data-component-end-line="253" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-2%20h-2%20rounded-sm%20flex-shrink-0%20transition-opacity%22%7D"
                        className="w-2 h-2 rounded-sm flex-shrink-0 transition-opacity"
                        style={{
                          background: laneColor,
                          opacity: isActive ? 1 : 0.3,
                        }}
                      />
                      <Icon data-component-id="src/app/components/SpecMapSidebar.tsx:254:22" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="254" data-component-end-line="258" data-component-file="SpecMapSidebar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22flex-shrink-0%22%7D"
                        size={13}
                        className="flex-shrink-0"
                        style={{ color: isActive ? laneColor : '#8A7F78' }}
                      />
                      <span data-component-id="src/app/components/SpecMapSidebar.tsx:259:22" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="259" data-component-end-line="265" data-component-file="SpecMapSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                        className={`text-xs flex-1 text-left ${
                          isActive ? 'text-foreground' : 'text-muted-foreground'
                        }`}
                      >
                        {lane.label}
                      </span>
                      <span data-component-id="src/app/components/SpecMapSidebar.tsx:266:22" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="266" data-component-end-line="268" data-component-file="SpecMapSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-2xs%20text-muted-foreground%20font-tabular%22%7D" className="text-2xs text-muted-foreground font-tabular">
                        {laneCounts[lane.id] ?? 0}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!collapsed && (
          <div data-component-id="src/app/components/SpecMapSidebar.tsx:278:10" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="278" data-component-end-line="305" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-3%22%7D" className="mt-3">
            <div data-component-id="src/app/components/SpecMapSidebar.tsx:279:12" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="279" data-component-end-line="281" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-3%20py-1.5%20text-2xs%20font-semibold%20uppercase%20tracking-widest%20text-muted-foreground%22%2C%22textContent%22%3A%22Recent%22%7D" className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-widest text-muted-foreground">
              Recent
            </div>
            <div data-component-id="src/app/components/SpecMapSidebar.tsx:282:12" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="282" data-component-end-line="304" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-1%20space-y-0.5%22%7D" className="px-1 space-y-0.5">
              {recentNodes.map((node) => (
                <button data-component-id="src/app/components/SpecMapSidebar.tsx:284:16" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="284" data-component-end-line="302" data-component-file="SpecMapSidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  key={`recent-${node.id}`}
                  onClick={() => onNodeSelect(node.id)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all hover:bg-muted/30 active:scale-[0.98] ${
                    selectedNodeId === node.id ? 'bg-primary/8' : ''
                  }`}
                >
                  <Circle data-component-id="src/app/components/SpecMapSidebar.tsx:291:18" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="291" data-component-end-line="298" data-component-file="SpecMapSidebar.tsx" data-component-name="Circle" data-component-content="%7B%22elementName%22%3A%22Circle%22%2C%22className%22%3A%22flex-shrink-0%22%7D"
                    size={6}
                    className="flex-shrink-0"
                    style={{
                      color: STATUS_COLOR[node.status] || '#8A7F78',
                      fill: STATUS_COLOR[node.status] || '#8A7F78',
                    }}
                  />
                  <span data-component-id="src/app/components/SpecMapSidebar.tsx:299:18" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="299" data-component-end-line="301" data-component-file="SpecMapSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20truncate%20flex-1%20text-left%22%7D" className="text-xs text-muted-foreground truncate flex-1 text-left">
                    {node.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div data-component-id="src/app/components/SpecMapSidebar.tsx:309:6" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="309" data-component-end-line="319" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`border-t border-border p-2 flex items-center gap-2 ${collapsed ? 'justify-center' : ''}`}>
        <div data-component-id="src/app/components/SpecMapSidebar.tsx:310:8" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="310" data-component-end-line="312" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-7%20h-7%20rounded-full%20bg-primary%20flex%20items-center%20justify-center%20text-2xs%20font-semibold%20text-primary-foreground%20flex-shrink-0%22%2C%22textContent%22%3A%22KW%22%7D" className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-2xs font-semibold text-primary-foreground flex-shrink-0">
          KW
        </div>
        {!collapsed && (
          <div data-component-id="src/app/components/SpecMapSidebar.tsx:314:10" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="314" data-component-end-line="317" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%20flex-1%22%7D" className="min-w-0 flex-1">
            <div data-component-id="src/app/components/SpecMapSidebar.tsx:315:12" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="315" data-component-end-line="315" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20font-medium%20text-foreground%20truncate%22%2C%22textContent%22%3A%22Kavinu%20Wethmin%22%7D" className="text-xs font-medium text-foreground truncate">Kavinu Wethmin</div>
            <div data-component-id="src/app/components/SpecMapSidebar.tsx:316:12" data-component-path="src/app/components/SpecMapSidebar.tsx" data-component-line="316" data-component-end-line="316" data-component-file="SpecMapSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-2xs%20text-muted-foreground%20truncate%22%2C%22textContent%22%3A%22Tech%20Lead%22%7D" className="text-2xs text-muted-foreground truncate">Tech Lead</div>
          </div>
        )}
      </div>
    </aside>
  );
}
